import React, { useState, useMemo } from 'react'
import Modal from '@mui/material/Modal'
import Backdrop from '@mui/material/Backdrop'
import Fade from '@mui/material/Fade'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import { alpha, styled } from '@mui/material/styles'
import InputBase from '@mui/material/InputBase'
import SearchIcon from '@mui/icons-material/Search'
import useGetRegisteredBabySitter from '@/services/hooks/useGetRegisteredBabySitter'
import { useChangeParticipantSupervisor } from '@/services/hooks/useChangeParticipantSupervisor'
import { SupervisorInfo } from '@/services/index'
import { UserInterface } from '@/services/types/APIResponseType'
import DummyImage from '@/assets/dummy-profile-image.svg'
import close_icon from '@/assets/close_icon.svg'

interface Props {
  open: boolean
  consumerId: string
  currentSupervisor: SupervisorInfo | null
  onClose: () => void
  onSuccess: (newSupervisor: SupervisorInfo) => void
}

const SearchWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.08),
  '&:hover': { backgroundColor: alpha(theme.palette.common.white, 0.12) },
  width: '100%',
}))

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}))

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  width: '100%',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
  },
}))

const boxStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: 'calc(100% - 24px)', sm: '90%', md: 520 },
  maxWidth: 520,
  maxHeight: { xs: '92vh', sm: '85vh' },
  outline: 'none',
  display: 'flex',
  flexDirection: 'column' as const,
  color: 'white',
  border: '1px solid rgb(75, 90, 135)',
  borderRadius: '20px',
  background: 'rgba(69, 68, 68, 0)',
  backdropFilter: 'blur(28px)',
  padding: { xs: '16px 12px', md: '22px 16px' },
  overflow: 'hidden',
}

const ChangeSupervisorModal: React.FC<Props> = ({
  open,
  consumerId,
  currentSupervisor,
  onClose,
  onSuccess,
}) => {
  const [search, setSearch] = useState('')
  const [selectedSupervisorId, setSelectedSupervisorId] = useState<
    string | null
  >(null)
  const [errorMsg, setErrorMsg] = useState('')

  const { data: babysittersData, isLoading: isFetching } =
    useGetRegisteredBabySitter({ limit: 200, page: 1, search: '' })

  const allSupervisors: UserInterface[] =
    babysittersData?.data?.babysittersProfiles ?? []

  const filteredSupervisors = useMemo(() => {
    const term = search.trim().toLowerCase().replace(/\s+/g, '')
    return allSupervisors.filter((s) => {
      if (s._id === currentSupervisor?.userId) return false
      if (!term) return true
      const fullName =
        `${s.profileInfo.firstName}${s.profileInfo.lastName}`.toLowerCase()
      return fullName.includes(term) || s.email.toLowerCase().includes(term)
    })
  }, [allSupervisors, search, currentSupervisor])

  const { mutate, isPending } = useChangeParticipantSupervisor()

  const handleSelect = (userId: string): void => {
    setSelectedSupervisorId((prev) => (prev === userId ? null : userId))
    setErrorMsg('')
  }

  const handleConfirm = (): void => {
    if (!selectedSupervisorId) {
      setErrorMsg('Please select a supervisor.')
      return
    }

    mutate(
      { consumerId, newSupervisorUserId: selectedSupervisorId },
      {
        onSuccess: () => {
          const selected = allSupervisors.find(
            (s) => s._id === selectedSupervisorId,
          )
          if (selected) {
            onSuccess({
              userId: selected._id,
              email: selected.email,
              role: selected.role,
              firstName: selected.profileInfo.firstName,
              lastName: selected.profileInfo.lastName,
              profilePic: selected.profileInfo.profilePic ?? '',
              phone: selected.profileInfo.phone ?? 0,
              countryCode: selected.profileInfo.countryCode ?? '+1',
            })
          }
          handleClose()
        },
        onError: (err: unknown) => {
          const msg =
            (err as { message?: string })?.message ??
            'Failed to change supervisor. Please try again.'
          setErrorMsg(msg)
        },
      },
    )
  }

  const handleClose = (): void => {
    setSearch('')
    setSelectedSupervisorId(null)
    setErrorMsg('')
    onClose()
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{ backdrop: { timeout: 400 } }}
    >
      <Fade in={open}>
        <Box sx={boxStyle}>
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1" />
            <p className="flex-1 text-center text-[22px] font-bold">
              Change Supervisor
            </p>
            <div className="flex-1 flex justify-end">
              <div
                className="w-fit h-fit p-[3px] cursor-pointer ml-auto"
                onClick={handleClose}
              >
                <img
                  src={close_icon}
                  alt="Close"
                  className="bg-[#ffffff1a] rounded-full"
                />
              </div>
            </div>
          </div>

          {/* Current supervisor context */}
          {currentSupervisor && (
            <p className="px-2 pb-3 text-[#959595] text-sm">
              Current:{' '}
              <span className="text-white font-medium">
                {currentSupervisor.firstName} {currentSupervisor.lastName}
              </span>
            </p>
          )}

          {/* Search */}
          <div className="px-2 pb-4">
            <SearchWrapper>
              <SearchIconWrapper>
                <SearchIcon sx={{ color: '#959595', fontSize: 20 }} />
              </SearchIconWrapper>
              <StyledInputBase
                placeholder="Search supervisors…"
                inputProps={{ 'aria-label': 'search supervisors' }}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ color: 'white' }}
              />
            </SearchWrapper>
          </div>

          {/* Supervisor list */}
          <div className="overflow-y-auto flex-1 max-h-[380px] px-2 pb-2">
            {isFetching ? (
              <div className="flex justify-center py-10">
                <CircularProgress sx={{ color: '#FB5012' }} />
              </div>
            ) : filteredSupervisors.length === 0 ? (
              <p className="text-center text-[#959595] py-8 text-sm">
                No supervisors available.
              </p>
            ) : (
              filteredSupervisors.map((s, idx) => {
                const isSelected = selectedSupervisorId === s._id
                const isLast = idx === filteredSupervisors.length - 1

                return (
                  <div
                    key={s._id}
                    onClick={() => handleSelect(s._id)}
                    className={`flex items-center gap-4 py-4 ${isLast ? '' : 'border-b border-white/10'} cursor-pointer hover:bg-white/5 rounded-lg px-2 transition-colors`}
                  >
                    <img
                      src={
                        s.profileInfo.profilePic
                          ? s.profileInfo.profilePic
                          : DummyImage
                      }
                      alt={s.profileInfo.firstName}
                      className="w-[52px] h-[52px] object-cover rounded-full border-2 border-white/30 flex-shrink-0"
                    />
                    <div className="flex flex-col flex-1 min-w-0">
                      <p className="text-white text-base font-medium truncate">
                        {s.profileInfo.firstName} {s.profileInfo.lastName}
                      </p>
                      <p className="text-[#959595] text-sm truncate">
                        {s.email}
                      </p>
                    </div>
                    <div
                      className={`w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-colors ${isSelected ? 'border-[#FB5012] bg-[#FB5012]' : 'border-white/30 bg-transparent'}`}
                    >
                      {isSelected && (
                        <div className="w-2 h-2 rounded-full bg-white" />
                      )}
                    </div>
                  </div>
                )
              })
            )}
          </div>

          {/* Error */}
          {errorMsg && (
            <p className="px-2 pt-2 text-[#FB5012] text-sm">{errorMsg}</p>
          )}

          {/* Footer */}
          <div className="px-2 pt-4 flex items-center justify-between gap-4 border-t border-white/10">
            <p className="text-[#959595] text-sm">
              {selectedSupervisorId
                ? `1 supervisor selected`
                : 'Select a new supervisor'}
            </p>
            <button
              onClick={handleConfirm}
              disabled={isPending || !selectedSupervisorId}
              className="flex items-center justify-center gap-2 bg-[#FB5012] text-white px-7 py-2.5 rounded-full text-lg capitalize disabled:opacity-40 hover:opacity-90 transition-opacity"
            >
              {isPending ? (
                <CircularProgress size={18} sx={{ color: '#fff' }} />
              ) : null}
              {isPending ? 'Saving…' : 'Confirm'}
            </button>
          </div>
        </Box>
      </Fade>
    </Modal>
  )
}

export { ChangeSupervisorModal }
